import moment from 'moment';
import { GetCurrencySymbol } from '../../../../core/utils/GetCurrenciesData';
import { GetMonths } from '../../../../core/utils/GetMonths';
import { GenerateEmptyMonthStatementsForAccount } from '../../../../core/utils/GenerateEmptyMonthStatements';
import { GenerateTwoYearMonthStatements } from '../../../../core/utils/GenerateMonthStatements';
import { COMMISSIONS_DETAILED_VIEW, COMMISSIONS_SUMMARY_VIEW, COMMISSIONS_NONE_VIEW } from '../../../../core/utils/Constants/Constants';
import { MonthStatementDataType } from '../../../../core/types/GenrateMonthStatements';
import {
	AccountCumulativeDetails,
	AccountDetailsType,
	AccountTabularDetails,
	BuildCommissionGraph
} from '../../../../core/types/AccountDetailsTypes';
import { MonthsObject } from '../../../../core/utils/GetMonths/GetMonths';
import { AscendingSort } from '../../../../core/utils/Formatter/AscendingSort';
import { CommissionViewType, Series, SeriesEvent, TradeSeriesField } from '../../../../core/types/Series';

type MonthWiseFieldsArrayType = {
	fieldName: string;
	fieldLabel: string;
	fieldType: string;
	fieldValue?: string | number;
}[];

function buildFields(entry: AccountCumulativeDetails, translate: Function) {
	const fields = [
		{
			fieldName: 'Referrals',
			fieldLabel: translate('account.section.annualsummary.column.referrals'),
			fieldValue: entry.numberofreferrals,
			fieldType: 'number',
		},
		{
			fieldName: 'Registrations',
			fieldLabel: translate('account.section.annualsummary.column.registrations'),
			fieldValue: entry.numberofregistrations,
			fieldType: 'number',
		},
		{
			fieldName: 'Traded Accounts',
			fieldLabel: translate('account.section.annualsummary.column.tradedaccounts'),
			fieldValue: entry.numberoftradedaccounts,
			fieldType: 'number',
		},
	];

	return fields;
}

function buildMultipleCommissionFields(translate: Function) {
	return [
		{
			fieldName: 'Referrals',
			fieldLabel: translate('account.section.annualsummary.column.referrals'),
			fieldValue: -1,
			fieldType: 'number',
		},
		{
			fieldName: 'Registrations',
			fieldLabel: translate('account.section.annualsummary.column.registrations'),
			fieldValue: -1,
			fieldType: 'number',
		},
		{
			fieldName: 'Traded Accounts',
			fieldLabel: translate('account.section.annualsummary.column.tradedaccounts'),
			fieldValue: -1,
			fieldType: 'number',
		},
	];
}

function isMultipleCommissionEntry(entry: AccountCumulativeDetails, summary: AccountTabularDetails, index: number) {
	if (index > 0) {
		const previousEntry = summary.account_cumulatives_details[index - 1],
			previousEntryMonth = previousEntry.calendarmonth,
			previousEntryYear = previousEntry.calendaryear;
		return previousEntry && entry.calendaryear === previousEntryYear && entry.calendarmonth === previousEntryMonth;
	}
	return false;
}

function buildMonthData(entry: AccountCumulativeDetails, currentMonth: number, commissionViewType: CommissionViewType, summary: AccountTabularDetails, index: number, translate: Function) {
	const date: moment.Moment = moment();
	date.date(1);
	date.month(entry.calendarmonth - 1);
	date.year(entry.calendaryear);

	const month: string = date.format('MMMM');
	const period: string = date.format('MMMM YYYY');
	const formattedPeriod: string = period.charAt(0).toUpperCase() + period.slice(1);

	const isMultipleCommission: boolean = isMultipleCommissionEntry(entry, summary, index);

	const event: SeriesEvent = {
		eventStart: isMultipleCommission ? '' : month,
		eventEnd: isMultipleCommission ? '' : month,
		eventLabel: isMultipleCommission ? '' : formattedPeriod,
		eventLabelSmall: isMultipleCommission ? '' : entry.calendarmonth !== currentMonth ? '' : entry.currency,
		eventYear: isMultipleCommission ? '' : entry.calendaryear,
	};

	const fields: TradeSeriesField[] = isMultipleCommission ? buildMultipleCommissionFields(translate) : buildFields(entry, translate);
	const isNoCommissionView: boolean = commissionViewType === COMMISSIONS_NONE_VIEW;

	if ((commissionViewType === COMMISSIONS_SUMMARY_VIEW || commissionViewType === COMMISSIONS_DETAILED_VIEW) && !isNoCommissionView) {
		const commissionsData = {
			fieldName: 'Commissions',
			fieldLabel: translate('account.section.annualsummary.column.commissions'),
			fieldValue: entry.commissionpaid,
			fieldType: 'currency',
			customAttributes: {
				currency: GetCurrencySymbol(entry.currency),
				paid: true,
			}
		};
		fields.push(commissionsData);
	}

	return {
		isMultipleCommission,
		event,
		fields,
	};
}

function buildTable(summary: AccountTabularDetails, commissionViewType: CommissionViewType, translate: Function) {
	if (summary.account_cumulatives_details) {
		let table: (false | { isMultipleCommission: boolean; event: SeriesEvent; fields: TradeSeriesField[]; })[];
		let currentMonth: number;
		let counter: number = 0;
		const isNoCommissionView: boolean = commissionViewType === COMMISSIONS_NONE_VIEW;

		table = summary.account_cumulatives_details.map((entry: AccountCumulativeDetails, index: number) => {
			if (!entry.calendarmonth || counter > 24) {
				return false;
			}

			if (entry.calendarmonth !== currentMonth) {
				counter++;
			}

			const monthData = buildMonthData(entry, currentMonth, commissionViewType, summary, index, translate);
			currentMonth = entry.calendarmonth;

			return monthData;
		}).filter((event: any) => {
			const showMultipleCommission: boolean = !!event && isNoCommissionView && event.isMultipleCommission;

			if (showMultipleCommission) {
				return false;
			}
			return !!event;
		});

		return table;
	}
}

export const convert = (summary: AccountTabularDetails, commissionViewType: CommissionViewType, translate: Function) => {
	const table = buildTable(summary, commissionViewType, translate);

	const summaries = {
		referrals: summary?.account_summary[0]?.yourreferrals,
		registrations: summary?.account_summary[0]?.registrations,
		accounts: summary?.account_summary[0]?.tradedaccounts
	};

	return {
		summaries,
		table
	};
};

function buildSeries(summary: AccountDetailsType, today: moment.Moment, view: string) {
	if (!summary.accountCommissionGraph) {
		return [];
	}
	const months: MonthsObject = GetMonths();
	let series: Array<Series> | [] = [];

	if (!(summary?.accountCommissionGraph && summary.accountCommissionGraph.length > 0)) {
		const monthStatements: MonthStatementDataType[] = GenerateTwoYearMonthStatements();
		let accountGraphDetailsObj = {
			commissionPaid: 0,
			approvedCommissionAmount: 0
		};
		summary.accountCommissionGraph = GenerateEmptyMonthStatementsForAccount(monthStatements, accountGraphDetailsObj);
	}

	summary.accountCommissionGraph.forEach((entry: any) => {
		const date: moment.Moment = moment();

		date.date(1);
		date.month(entry.calendarMonth - 1);
		date.year(entry.calendarYear);

		const month: string = date.format('MMM');
		const formattedPeriod: string = month.charAt(0).toUpperCase() + month.slice(1);
		const year: string = date.format('YYYY');

		today.date(1);
		const thisYear: string = today.format('YYYY');
		const monthWiseFieldsArray: MonthWiseFieldsArrayType = months[formattedPeriod].fields;

		if (months[formattedPeriod]) {
			const field: any = {
				fieldName: year,
				fieldLabel: year,
				fieldType: 'number'
			};
			if (thisYear === year && entry.calendarMonth - 1 > today.month()) {
				monthWiseFieldsArray.push(field);
			} else {
				field.fieldValue = view === 'paid' ? entry.commissionPaid : entry.approvedCommissionAmount;
				monthWiseFieldsArray.push(field);
			}
		}
	});

	series = AscendingSort(months) as Array<Series>;

	return series;
}

const buildCommissions = (summary: AccountDetailsType, today: moment.Moment): BuildCommissionGraph => {
	const hasCumulatives: boolean = summary.accountCumulatives?.length > 0;
	const paymentDate = moment().date(5);

	if (!hasCumulatives) {
		return {
			balance: 0,
			date: today > paymentDate ? paymentDate.add(1, 'M') : paymentDate,
			generatedMonthly: 0,
			generatedQuarterly: 0,
			generatedMonthlyChange: '0.00 (0.00%)',
			generatedQuarterlyChange: '0.00 (0.00%)',
			paidMonthly: 0,
			paidQuarterly: 0,
			paidMonthlyChange: '0.00 (0.00%)',
			paidQuarterlyChange: '0.00 (0.00%)',
			generatedLastQuarterOfPrevYear: 0,
			paidLastQuarterOfPrevYear: 0,
			generatedLastMonthOfPrevYear: 0,
			paidLastMonthOfPrevYear: 0,
			balancecarriedforward: 0,
			generatedcommisioncurrentmonth: 0,
			paidcommisioncurrentmonth: 0,
		};
	}

	const {
		dateCommissionToBePaid,
		commGeneratedLastMonth,
		commGeneratedLastQuarter,
		percentDifferenceCommGeneratedLastMonthOfPrevYear,
		percentDifferenceCommGeneratedLastQuarterOfPrevYear,
		commPaidLastMonth,
		commPaidLastQuarter,
		percentDifferenceCommPaidLastMonthOfPrevYear,
		percentDifferenceCommPaidLastQuarterOfPrevYear,
		commGeneratedLastQuarterOfPrevYear,
		commPaidLastQuarterOfPrevYear,
		commGeneratedLastMonthOfPrevYear,
		commPaidLastMonthOfPrevYear,
		balanceCarriedForward,
		commGeneratedCurrentMonth,
	} = summary.accountCumulatives[0];

	return {
		date: dateCommissionToBePaid || (today > paymentDate ? paymentDate.add(1, 'M') : paymentDate),
		generatedMonthly: commGeneratedLastMonth,
		generatedQuarterly: commGeneratedLastQuarter,
		generatedMonthlyChange: percentDifferenceCommGeneratedLastMonthOfPrevYear || '0.00 (0.00%)',
		generatedQuarterlyChange: percentDifferenceCommGeneratedLastQuarterOfPrevYear ?? '0.00 (0.00%)',
		paidMonthly: commPaidLastMonth,
		paidQuarterly: commPaidLastQuarter,
		paidMonthlyChange: percentDifferenceCommPaidLastMonthOfPrevYear || '0.00 (0.00%)',
		paidQuarterlyChange: percentDifferenceCommPaidLastQuarterOfPrevYear ?? '0.00 (0.00%)',
		generatedLastQuarterOfPrevYear: commGeneratedLastQuarterOfPrevYear,
		paidLastQuarterOfPrevYear: commPaidLastQuarterOfPrevYear,
		generatedLastMonthOfPrevYear: commGeneratedLastMonthOfPrevYear,
		paidLastMonthOfPrevYear: commPaidLastMonthOfPrevYear,
		balancecarriedforward: balanceCarriedForward,
		generatedcommisioncurrentmonth: commGeneratedCurrentMonth,
		paidcommisioncurrentmonth: commPaidLastMonth,
	};

}

export const convertCommissions = (summary: AccountDetailsType, view: string) => {
	const today: moment.Moment = moment();
	const series: Series[] = buildSeries(summary, today, view);
	const commissions: BuildCommissionGraph = buildCommissions(summary, today);

	return {
		commissions,
		series,
	};
};